/**
 * AST to JSON Transformer for Natural DSL
 *
 * Converts natural DSL AST to portable JSON format
 */
import { ExerciseDocument, ContentBlock } from '../ast/document';
import { LineBlock } from '../ast/blocks';
import { NumberLineBlock } from '../ast/number-line';
import { TableBlock } from '../ast/table';
import { RadioOptionsBlock } from '../ast/radio-options';
import { MatcherBlock } from '../ast/matcher';
import { MatcherItem } from '../ast/matcher-item';
import { PairMatcherBlock } from '../ast/pair-matcher';
import { InlineNode } from '../ast/inline';
import { ValidationNode } from '../ast/validation';
import { VerticalOperationBlock, VopBlockItem, VopRow, VopCarryRow } from '../ast/vertical-operation';
import type { ExerciseJSON, LayoutItem, ValidationJSON, WidgetJSON } from './json-schema';
import type { TableItem, TableCellItem, TableRowItem, NumberLineItem, NumberLinePositionItem, NumberLineCounterItem, NumberLineHighlightItem, NumberLineCursorItem, NumberLineArrowItem, MatcherWidgetJSON, MatcherRowItem, MatcherItemContent, PairMatcherWidgetJSON, VerticalOperationItem, VopRowItem, VopCarryRowItem, VopSeparatorItem, VopCellItem } from './zod-schemas';
import { AST_TYPES, JSON_TYPES, VALIDATION_MODES, INPUT_TYPES, INLINE_DRAG_OPT_PREFIX, RULER_WIDGET_ID, PROTRACTOR_WIDGET_ID, SET_SQUARE_WIDGET_ID } from '../constants';

/**
 * ID Generator for layout items
 * Generates unique, deterministic IDs for each layout item
 */
class LayoutIdGenerator {
  private counter: number = 0;

  reset(): void {
    this.counter = 0;
  }

  generate(type: string): string {
    return `${type}_${++this.counter}`;
  }
}

/**
 * Transform AST to JSON
 *
 * The JSON format is what gets consumed by renderers and validators
 */
export function toJSON(ast: ExerciseDocument): ExerciseJSON {
  // Reset ID generator for each transformation
  const idGenerator = new LayoutIdGenerator();
  // Stores widget definitions and their expected answers, populated across the collection phases below.
  const widgets: Record<string, WidgetJSON> = {};
  const answers: Record<string, string | number | string[][] | number[]> = {};

  // Pre-pass: assign a unique widgetId to every INLINE_DRAG_OPTION node by
  // reference, using a sequential global counter (same pattern as idGenerator).
  // Both the layout build and the widget collection loop read from this same
  // map so layout widget-refs and widget entries are always in sync.
  // The array is cached here and reused below to avoid walking the AST twice.
  const allInlines = collectAllInlines(ast.lines);
  const inlineDragIds = new Map<InlineNode, string>();
  {
    let counter = 0;
    for (const inline of allInlines) {
      if (inline.type === AST_TYPES.INLINE_DRAG_OPTION) {
        inlineDragIds.set(inline, `${INLINE_DRAG_OPT_PREFIX}${counter++}`);
      }
    }
  }

  // Build layout from question and lines
  const layout: LayoutItem[] = [];

  // Add question (if present)
  if (ast.question) {
    layout.push({
      id: idGenerator.generate(JSON_TYPES.QUESTION_TEXT),
      type: JSON_TYPES.QUESTION_TEXT,
      value: ast.question.value
    });
  }

  // Add content blocks (lines, tables, and radio options)
  for (const block of ast.lines) {
    if (block.type === AST_TYPES.TABLE_BLOCK) {
      layout.push(transformTable(block as TableBlock, idGenerator, inlineDragIds));
    } else if (block.type === AST_TYPES.NUMBER_LINE_BLOCK) {
      const nlItem = transformNumberLine(block as NumberLineBlock, idGenerator, inlineDragIds);
      layout.push(nlItem);
      // Collect counter answer inline — no second pass over layout needed.
      if (nlItem.counter?.answer !== undefined) {
        answers[nlItem.counter.id] = nlItem.counter.answer;
      }
      // Collect cursor answers (every cursor has a required answer).
      for (const cursor of nlItem.cursors ?? []) {
        answers[cursor.id] = cursor.answer;
      }
    } else if (block.type === AST_TYPES.MATCHER_BLOCK) {
      const matcherBlock = block as MatcherBlock;
      layout.push({
        id: idGenerator.generate(JSON_TYPES.WIDGET_REF),
        type: JSON_TYPES.WIDGET_REF,
        widgetId: matcherBlock.name,
      });
      layout.push({
        id: idGenerator.generate(JSON_TYPES.LINEBREAK),
        type: JSON_TYPES.LINEBREAK,
      });
      // widget and answers are collected in the loop below
    } else if (block.type === AST_TYPES.PAIR_MATCHER_BLOCK) {
      const pmBlock = block as PairMatcherBlock;
      layout.push({
        id: idGenerator.generate(JSON_TYPES.WIDGET_REF),
        type: JSON_TYPES.WIDGET_REF,
        widgetId: pmBlock.name,
      });
      layout.push({
        id: idGenerator.generate(JSON_TYPES.LINEBREAK),
        type: JSON_TYPES.LINEBREAK,
      });
      // widget and answers collected below
    } else if (block.type === AST_TYPES.VERTICAL_OPERATION_BLOCK) {
      layout.push(transformVerticalOperation(block as VerticalOperationBlock, idGenerator));
    } else if (block.type === AST_TYPES.RADIO_OPTIONS) {
      const radioBlock = block as RadioOptionsBlock;
      layout.push({
        id: idGenerator.generate(JSON_TYPES.WIDGET_REF),
        type: JSON_TYPES.WIDGET_REF,
        widgetId: radioBlock.name
      });
      layout.push({
        id: idGenerator.generate(JSON_TYPES.LINEBREAK),
        type: JSON_TYPES.LINEBREAK
      });
    } else {
      const lineItems = transformLine(block as LineBlock, idGenerator, inlineDragIds);
      layout.push(...lineItems);
    }
  }
  // Collect widgets and answers from inline inputs (across both lines and table cells)
  for (const inline of allInlines) {
    if (inline.type === AST_TYPES.INLINE_INPUT) {
      if (inline.inputType === INPUT_TYPES.STRING) {
        widgets[inline.name] = {
          type: JSON_TYPES.INLINE_INPUT,
          inputType: INPUT_TYPES.STRING,
          length: inline.length,
          placeholder: inline.placeholder,
        };
      } else {
        widgets[inline.name] = {
          type: JSON_TYPES.INLINE_INPUT,
          inputType: INPUT_TYPES.NUMERIC,
          digits: inline.digits,
          placeholder: inline.placeholder,
        };
      }

      if (inline.answer !== undefined) {
        answers[inline.name] = inline.answer;
      }
    } else if (inline.type === AST_TYPES.CLOCK && inline.mode === 'interactive') {
      answers[inline.name] = inline.answer;
    }

    if (inline.type === AST_TYPES.FRACTION) {
      widgets[inline.name] = {
        type: JSON_TYPES.FRACTION,
        numerator: inline.numerator ?? null,
        denominator: inline.denominator ?? null,
        simplified: inline.simplified ?? true,
        placeholder: inline.placeholder,
      } as any;

      if (inline.answer !== undefined) {
        answers[inline.name] = inline.answer;
      }
    }

    if (inline.type === AST_TYPES.INLINE_DRAG_OPTION) {
      const widgetId = inlineDragIds.get(inline)!;
      widgets[widgetId] = {
        type: JSON_TYPES.INLINE_DRAG_OPTION,
        value: inline.value,
      };
    }

    if (inline.type === AST_TYPES.VISUAL_FRACTION) {
      const answerArr = inline.answer ?? [];
      widgets[inline.name] = {
        type: JSON_TYPES.VISUAL_FRACTION,
        shape: inline.shape,
        divisions: inline.divisions,
        filled: inline.filled ?? [],
        answer: answerArr,
        ...(inline.orientation !== undefined && { orientation: inline.orientation }),
        ...(inline.label !== undefined && { label: inline.label }),
        ...(inline.size !== undefined && { size: inline.size }),
      } satisfies import('./zod-schemas').VisualFractionJSON;

      if (answerArr.length > 0) {
        answers[inline.name] = answerArr;
      }
    }
  }

  // Collect widgets + answers from VERTICAL_OPERATION input cells
  for (const block of ast.lines) {
    if (block.type !== AST_TYPES.VERTICAL_OPERATION_BLOCK) continue;
    const vop = block as VerticalOperationBlock;
    for (const item of vop.items) {
      if (item.type !== AST_TYPES.VOP_ROW && item.type !== AST_TYPES.VOP_CARRY_ROW) continue;
      for (const cell of (item as VopRow | VopCarryRow).cells) {
        if (cell.kind !== 'input') continue;
        if (widgets[cell.name]) {
          throw new Error(`VERTICAL_OPERATION: duplicate INPUT name "${cell.name}"`);
        }
        widgets[cell.name] = {
          type: JSON_TYPES.INLINE_INPUT,
          inputType: INPUT_TYPES.NUMERIC,
          digits: cell.digits,
        };
        if (cell.answer !== undefined) {
          answers[cell.name] = cell.answer;
        }
      }
    }
  }

  // Collect widgets and answers from radio options blocks
  for (const block of ast.lines) {
    if (block.type === AST_TYPES.RADIO_OPTIONS) {
      const radioBlock = block as RadioOptionsBlock;
      widgets[radioBlock.name] = {
        type: JSON_TYPES.RADIO_OPTIONS,
        options: radioBlock.options
      };
      answers[radioBlock.name] = radioBlock.answer;
    } else if (block.type === AST_TYPES.MATCHER_BLOCK) {
      const matcherBlock = block as MatcherBlock;
      widgets[matcherBlock.name] = buildMatcherWidget(matcherBlock, idGenerator, inlineDragIds);
      answers[matcherBlock.name] = matcherBlock.answers;
    } else if (block.type === AST_TYPES.PAIR_MATCHER_BLOCK) {
      const pmBlock = block as PairMatcherBlock;
      widgets[pmBlock.name] = buildPairMatcherWidget(pmBlock, idGenerator, inlineDragIds);
      answers[pmBlock.name] = pmBlock.answers;
    }
  }

  // Add tools to widgets
  if (ast.tools) {
    for (const tool of ast.tools) {
      if (tool.type === AST_TYPES.DRAG_OPTIONS) {
        widgets['_drag_options'] = {
          type: JSON_TYPES.DRAG_OPTIONS,
          options: tool.options
        };
      } else if (tool.type === AST_TYPES.NUMERIC_KEYPAD) {
        widgets['_numeric_keypad'] = {
          type: JSON_TYPES.NUMERIC_KEYPAD,
          includeOperators: tool.includeOperators,
          includeDecimal: tool.includeDecimal,
          style: tool.style
        };
      } else if (tool.type === AST_TYPES.GEOMETRY_RULER) {
        widgets[RULER_WIDGET_ID] = {
          type: JSON_TYPES.RULER,
          size: tool.size
        };
      } else if (tool.type === AST_TYPES.GEOMETRY_PROTRACTOR) {
        widgets[PROTRACTOR_WIDGET_ID] = {
          type: JSON_TYPES.PROTRACTOR,
          size: tool.size
        };
      } else if (tool.type === AST_TYPES.GEOMETRY_SET_SQUARE) {
        widgets[SET_SQUARE_WIDGET_ID] = {
          type: JSON_TYPES.SET_SQUARE,
          size: tool.size
        };
      }
    }
  }

  // Transform validation (optional - not present for display-only exercises)
  return {
    version: '2.0',  // Natural DSL version
    layout,
    widgets,
    ...(ast.validation ? { validation: transformValidation(ast.validation, answers, widgets) } : {}),
  };
}

/**
 * Collect all inline nodes from content blocks (lines and table cells)
 */
function collectAllInlines(blocks: ContentBlock[]): InlineNode[] {
  const result: InlineNode[] = [];
  for (const block of blocks) {
    if (block.type === AST_TYPES.LINE_BLOCK) {
      result.push(...(block as LineBlock).inlines);
    } else if (block.type === AST_TYPES.TABLE_BLOCK) {
      for (const row of (block as TableBlock).rows) {
        for (const cell of row.cells) {
          result.push(...cell.inlines);
        }
      }
    } else if (block.type === AST_TYPES.NUMBER_LINE_BLOCK) {
      const numberLine = block as NumberLineBlock;
      for (const position of numberLine.positions) {
        result.push(...position.inlines);
      }
      for (const arrow of numberLine.arrows ?? []) {
        result.push(...arrow.inlines);
      }
    } else if (block.type === AST_TYPES.MATCHER_BLOCK) {
      for (const row of (block as MatcherBlock).rows) {
        for (const item of row.items) {
          result.push(...(item.inlines as InlineNode[]));
        }
      }
    } else if (block.type === AST_TYPES.PAIR_MATCHER_BLOCK) {
      const pm = block as PairMatcherBlock;
      for (const item of [...pm.topItems, ...pm.bottomItems]) {
        result.push(...(item.inlines as InlineNode[]));
      }
    }
  }
  return result;
}

/**
 * Transform a number line block into a layout item
 */
function transformNumberLine(numberLine: NumberLineBlock, idGenerator: LayoutIdGenerator, inlineDragIds: Map<InlineNode, string>): NumberLineItem {
  // ── 4. Positions (content) — built first so ids are numerically lowest ───
  const positions: NumberLinePositionItem[] = numberLine.positions.map(pos => {
    const content = pos.inlines.map(inline => transformInline(inline, idGenerator, inlineDragIds));
    return {
      id: idGenerator.generate(JSON_TYPES.NUMBER_LINE_POSITION),
      type: JSON_TYPES.NUMBER_LINE_POSITION,
      value: pos.value,
      content
    };
  });

  // ── 3b. Counter overlay ───────────────────────────────────────────────────
  let counterItem: NumberLineCounterItem | undefined;
  if (numberLine.counter !== undefined) {
    const c = numberLine.counter;
    counterItem = {
      id:   idGenerator.generate(JSON_TYPES.NUMBER_LINE_COUNTER),
      type: JSON_TYPES.NUMBER_LINE_COUNTER,
      min:  c.min,
      max:  c.max,
      ...(c.default !== undefined && { default: c.default }),
      ...(c.name    !== undefined && { name:    c.name }),
      ...(c.answer  !== undefined && { answer:  c.answer }),
    };
  }

  // ── 3c. Highlight overlays ────────────────────────────────────────────────
  let highlights: NumberLineHighlightItem[] | undefined;
  if (numberLine.highlights !== undefined && numberLine.highlights.length > 0) {
    highlights = numberLine.highlights.map(h => ({
      id:   idGenerator.generate(JSON_TYPES.NUMBER_LINE_HIGHLIGHT),
      type: JSON_TYPES.NUMBER_LINE_HIGHLIGHT,
      from: h.from,
      to:   h.to,
      ...(h.color !== undefined && { color: h.color }),
      ...(h.label !== undefined && { label: h.label }),
    }));
  }

  // ── 3d. Cursor overlays ───────────────────────────────────────────────────
  let cursors: NumberLineCursorItem[] | undefined;
  if (numberLine.cursors !== undefined && numberLine.cursors.length > 0) {
    cursors = numberLine.cursors.map(c => ({
      id:        idGenerator.generate(JSON_TYPES.NUMBER_LINE_CURSOR),
      type:      JSON_TYPES.NUMBER_LINE_CURSOR,
      value:     c.value,
      answer:    c.answer,
      fillRange: c.fillRange,
      ...(c.color !== undefined && { color: c.color }),
      ...(c.label !== undefined && { label: c.label }),
    }));
  }
  // ── 3e. Arrow annotations ────────────────────────────────────────────────
  let arrows: NumberLineArrowItem[] | undefined;
  if (numberLine.arrows !== undefined && numberLine.arrows.length > 0) {
    arrows = numberLine.arrows.map(a => ({
      id:      idGenerator.generate(JSON_TYPES.NUMBER_LINE_ARROW),
      type:    JSON_TYPES.NUMBER_LINE_ARROW,
      from:    a.from,
      to:      a.to,
      ...(a.color !== undefined && { color: a.color }),
      content: a.inlines.map(inline => transformInline(inline, idGenerator, inlineDragIds)),
    }));
  }

  // ── Assemble in logical key order ────────────────────────────────────────
  //   1. Core range   : id, type, min, max, step
  //   2. Visual       : minorTicks?, style?, labelVisibility?
  //   3. Overlays     : counter?, highlights?, cursors? , arrows?
  //   4. Content      : positions
  const item: NumberLineItem = {
    id:   idGenerator.generate(JSON_TYPES.NUMBER_LINE),
    type: JSON_TYPES.NUMBER_LINE,
    min:  numberLine.min,
    max:  numberLine.max,
    step: numberLine.step,
    ...(numberLine.minorTicks       !== undefined && { minorTicks:       numberLine.minorTicks }),
    ...(numberLine.style            !== undefined && { style:            numberLine.style }),
    ...(numberLine.labelVisibility  !== undefined && { labelVisibility:  numberLine.labelVisibility }),
    ...(counterItem           !== undefined && { counter:    counterItem }),
    ...(highlights            !== undefined && { highlights }),
    ...(cursors               !== undefined && { cursors }),
    ...(arrows                !== undefined && { arrows }),
    ...(numberLine.dragMode   !== undefined && { dragMode:   numberLine.dragMode }),
    positions,
  };

  return item satisfies NumberLineItem;
}

/**
 * Serialise a flat list of MatcherItem AST nodes into MatcherItemContent JSON
 * objects.  Shared by buildMatcherWidget (per-row items) and
 * buildPairMatcherWidget (top/bottom flat lists).
 */
function mapMatcherItemList(
  items: MatcherItem[],
  idGenerator: LayoutIdGenerator,
  inlineDragIds: Map<InlineNode, string>,
): MatcherItemContent[] {
  return items.map(item => ({
    id: idGenerator.generate(JSON_TYPES.MATCHER_ITEM),
    type: JSON_TYPES.MATCHER_ITEM,
    itemId: item.itemId,
    content: item.inlines.map(inline => transformInline(inline as InlineNode, idGenerator, inlineDragIds)),
  }));
}

/**
 * Build a matcher widget entry (goes into the widgets record, not the layout).
 * The matcher name is used as the key in widgets, like radio-options.
 */
function buildMatcherWidget(matcher: MatcherBlock, idGenerator: LayoutIdGenerator, inlineDragIds: Map<InlineNode, string>): MatcherWidgetJSON {
  const rows: MatcherRowItem[] = matcher.rows.map(row => ({
    id: idGenerator.generate(JSON_TYPES.MATCHER_ROW),
    type: JSON_TYPES.MATCHER_ROW,
    items: mapMatcherItemList(row.items, idGenerator, inlineDragIds),
  } satisfies MatcherRowItem));

  const result: MatcherWidgetJSON = {
    type: JSON_TYPES.MATCHER,
    rows,
    groupCount: matcher.answers.length,
  };
  if (matcher.colors !== undefined) {
    result.colors = matcher.colors;
  }
  return result;
}

/**
 * Build a pair-matcher widget entry.
 * Top and bottom items are serialised identically to matcher items via the
 * shared mapMatcherItemList helper.
 */
function buildPairMatcherWidget(pm: PairMatcherBlock, idGenerator: LayoutIdGenerator, inlineDragIds: Map<InlineNode, string>): PairMatcherWidgetJSON {
  return {
    type: JSON_TYPES.PAIR_MATCHER,
    topItems: mapMatcherItemList(pm.topItems, idGenerator, inlineDragIds),
    bottomItems: mapMatcherItemList(pm.bottomItems, idGenerator, inlineDragIds),
  };
}

function transformVerticalOperation(
  vop: VerticalOperationBlock,
  idGenerator: LayoutIdGenerator
): VerticalOperationItem {
  const anyRowHasOp = vop.items.some(
    (item) => item.type === AST_TYPES.VOP_ROW && (item as VopRow).op !== undefined
  );
  if (!anyRowHasOp && vop.op === undefined) {
    throw new Error('VERTICAL_OPERATION: at least one op is required (set op on the block or on individual ROWs)');
  }

  let cols = 1;
  for (const item of vop.items) {
    if (item.type === AST_TYPES.VOP_ROW || item.type === AST_TYPES.VOP_CARRY_ROW) {
      cols = Math.max(cols, (item as VopRow | VopCarryRow).cells.length);
    }
  }

  function buildCells(row: VopRow | VopCarryRow): VopCellItem[] {
    return row.cells.map((cell) => {
      const id = idGenerator.generate(JSON_TYPES.VOP_CELL);
      if (cell.kind === 'digit') return { id, type: JSON_TYPES.VOP_CELL, kind: 'digit', value: cell.value };
      if (cell.kind === 'label') return { id, type: JSON_TYPES.VOP_CELL, kind: 'label', value: cell.value };
      if (cell.kind === 'color') return { id, type: JSON_TYPES.VOP_CELL, kind: 'color', value: cell.value, color: cell.color };
      if (cell.kind === 'bold') return { id, type: JSON_TYPES.VOP_CELL, kind: 'bold', value: cell.value };
      if (cell.kind === 'highlight') return { id, type: JSON_TYPES.VOP_CELL, kind: 'highlight', value: cell.value };
      if (cell.kind === 'input') return { id, type: JSON_TYPES.VOP_CELL, kind: 'input', widgetId: cell.name };
      return { id, type: JSON_TYPES.VOP_CELL, kind: 'empty' };
    });
  }

  const items: Array<VopRowItem | VopCarryRowItem | VopSeparatorItem> = vop.items.map((item: VopBlockItem) => {
    if (item.type === AST_TYPES.VOP_SEPARATOR) {
      return {
        id: idGenerator.generate(JSON_TYPES.VOP_SEPARATOR),
        type: JSON_TYPES.VOP_SEPARATOR,
      } satisfies VopSeparatorItem;
    }
    if (item.type === AST_TYPES.VOP_CARRY_ROW) {
      return {
        id: idGenerator.generate(JSON_TYPES.VOP_CARRY_ROW),
        type: JSON_TYPES.VOP_CARRY_ROW,
        cells: buildCells(item as VopCarryRow),
      } satisfies VopCarryRowItem;
    }
    const row = item as VopRow;
    return {
      id: idGenerator.generate(JSON_TYPES.VOP_ROW),
      type: JSON_TYPES.VOP_ROW,
      cells: buildCells(row),
      ...(row.op !== undefined && { op: row.op }),
    } satisfies VopRowItem;
  });

  return {
    id: idGenerator.generate(JSON_TYPES.VERTICAL_OPERATION),
    type: JSON_TYPES.VERTICAL_OPERATION,
    op: vop.op,
    cols,
    items,
  } satisfies VerticalOperationItem;
}

function transformTable(table: TableBlock, idGenerator: LayoutIdGenerator, inlineDragIds: Map<InlineNode, string>): TableItem {
  const rows: TableRowItem[] = table.rows.map(row => {
    const cells: TableCellItem[] = row.cells.map(cell => {
      const items = cell.inlines.map(inline => transformInline(inline, idGenerator, inlineDragIds));
      const cellItem: TableCellItem = {
        id: idGenerator.generate(JSON_TYPES.TABLE_CELL),
        type: JSON_TYPES.TABLE_CELL,
        items
      };
      if (cell.colspan !== undefined) {
        cellItem.colspan = cell.colspan;
      }
      return cellItem;
    });

    return {
      id: idGenerator.generate(JSON_TYPES.TABLE_ROW),
      type: JSON_TYPES.TABLE_ROW,
      cells
    } satisfies TableRowItem;
  });

  return {
    id: idGenerator.generate(JSON_TYPES.TABLE),
    type: JSON_TYPES.TABLE,
    cols: table.cols,
    rows
  } satisfies TableItem;
}

/**
 * Transform a line block into layout items
 */
function transformLine(line: LineBlock, idGenerator: LayoutIdGenerator, inlineDragIds: Map<InlineNode, string>): LayoutItem[] {
  const items: LayoutItem[] = [];

  for (const inline of line.inlines) {
    items.push(transformInline(inline, idGenerator, inlineDragIds));
  }

  // Add implicit line break
  items.push({
    id: idGenerator.generate(JSON_TYPES.LINEBREAK),
    type: JSON_TYPES.LINEBREAK
  });

  return items;
}

/**
 * Transform an inline element into a layout item
 */
function transformInline(inline: InlineNode, idGenerator: LayoutIdGenerator, inlineDragIds: Map<InlineNode, string>): LayoutItem {
  switch (inline.type) {
    case AST_TYPES.FREE_TEXT: {
      const trimmed = inline.value.trim();
      const operators = ['+', '-', '×', '÷', '=', '<', '>', '?'];

      if (operators.includes(trimmed) && inline.value.trim() === inline.value) {
        return {
          id: idGenerator.generate('operator'),
          type: 'operator',
          operator: trimmed
        };
      }

      return {
        id: idGenerator.generate(JSON_TYPES.TEXT),
        type: JSON_TYPES.TEXT,
        value: inline.value
      };
    }

    case AST_TYPES.SPACE:
      return {
        id: idGenerator.generate(JSON_TYPES.SPACE),
        type: JSON_TYPES.SPACE
      };

    case AST_TYPES.HIGHLIGHTED_TEXT:
      return {
        id: idGenerator.generate(JSON_TYPES.HIGHLIGHTED_TEXT),
        type: JSON_TYPES.HIGHLIGHTED_TEXT,
        value: inline.value,
        highlight: inline.highlight
      };

    case AST_TYPES.COLOR_TEXT:
      return {
        id: idGenerator.generate(JSON_TYPES.COLOR_TEXT),
        type: JSON_TYPES.COLOR_TEXT,
        value: inline.value,
        color: inline.color,
        highlight: inline.highlight
      };

    case AST_TYPES.BOLD_TEXT:
      return {
        id: idGenerator.generate(JSON_TYPES.BOLD_TEXT),
        type: JSON_TYPES.BOLD_TEXT,
        value: inline.value,
        highlight: inline.highlight
      };

    case AST_TYPES.INLINE_INPUT:
      return {
        id: idGenerator.generate(JSON_TYPES.WIDGET_REF),
        type: JSON_TYPES.WIDGET_REF,
        widgetId: inline.name
      };

    case AST_TYPES.FRACTION:
      return {
        id: idGenerator.generate(JSON_TYPES.WIDGET_REF),
        type: JSON_TYPES.WIDGET_REF,
        widgetId: inline.name
      };

    case AST_TYPES.IMAGE:
      return {
        id: idGenerator.generate(JSON_TYPES.IMAGE),
        type: JSON_TYPES.IMAGE,
        name: inline.name,
        src: inline.src,
        alt: inline.alt,
        ...(inline.width !== undefined && { width: inline.width }),
        ...(inline.height !== undefined && { height: inline.height }),
      };

    case AST_TYPES.VISUAL_FRACTION:
      return {
        id: idGenerator.generate(JSON_TYPES.WIDGET_REF),
        type: JSON_TYPES.WIDGET_REF,
        widgetId: inline.name
      };

    case AST_TYPES.INLINE_DRAG_OPTION:
      return {
        id: idGenerator.generate(JSON_TYPES.WIDGET_REF),
        type: JSON_TYPES.WIDGET_REF,
        widgetId: inlineDragIds.get(inline)!,
      };

    case AST_TYPES.CLOCK: {
      const base = {
        id: idGenerator.generate(JSON_TYPES.CLOCK),
        type: JSON_TYPES.CLOCK,
        mode: inline.mode,
        clockType: inline.clockType,
        value: inline.value,
        ...(inline.format !== undefined && { format: inline.format }),
        ...(inline.name !== undefined && { name: inline.name }),
      };
      if (inline.mode === 'interactive') {
        return { ...base, ...(inline.snap !== undefined && { snap: inline.snap }) };
      }
      return base;
    }

    default:
      throw new Error(`Unknown inline type: ${(inline as any).type}`);
  }
}

/**
 * Transform validation node into JSON validation
 */
function transformValidation(
  validation: ValidationNode,
  answers: Record<string, string | number | string[][] | number[]>,
  widgets: Record<string, WidgetJSON>
): ValidationJSON {
  if (validation.type === AST_TYPES.SIMPLE_VALIDATION) {
    return {
      mode: VALIDATION_MODES.SIMPLE,
      answers
    };
  } else if (validation.type === AST_TYPES.EXPRESSION_VALIDATION) {
    // Get valid variable names from widgets (these are the actual input names)
    const validVariableNames = Object.keys(widgets);

    // Extract variables from the expression, filtered by valid widget names
    const variables = extractVariables(validation.expression, validVariableNames);

    return {
      mode: VALIDATION_MODES.EXPRESSION,
      expression: validation.expression,
      variables
    };
  } else if (validation.type === AST_TYPES.MIXED_VALIDATION) {
    const validVariableNames = Object.keys(widgets);
    const variables = extractVariables(validation.expression, validVariableNames);

    // Dead-widget guard: any interactive widget without an `answer` and not
    // referenced by the expression is unreachable in mixed mode. Display-only
    // widgets (fully-literal fraction, filled visual-fraction with no answer)
    // are still allowed for hint/illustration purposes.
    const inExpression = new Set(variables);
    for (const [name, widget] of Object.entries(widgets)) {
      if (name.startsWith('_')) continue; // tools (e.g. _drag_options, _numeric_keypad)
      if (name in answers) continue;
      if (inExpression.has(name)) continue;

      // Display-only allowances (no answer expected):
      //   FRACTION with both literal numerator AND denominator → static display
      if (widget.type === JSON_TYPES.FRACTION) {
        const w = widget as any;
        if (w.numerator !== null && w.denominator !== null) continue;
      }
      //   VISUAL_FRACTION with `filled` set and no `answer` → static display
      if (widget.type === JSON_TYPES.VISUAL_FRACTION) {
        const w = widget as any;
        if (Array.isArray(w.filled) && w.filled.length > 0) continue;
      }

      throw new Error(
        `Mixed validation: widget "${name}" has no \`answer\` and is not referenced in expression "${validation.expression}"`
      );
    }

    return {
      mode: VALIDATION_MODES.MIXED,
      expression: validation.expression,
      answers,
      variables
    };
  }

  throw new Error(`Unknown validation type: ${(validation as any).type}`);
}

/**
 * Extract variable names from an expression
 * Simple regex-based extraction
 *
 * @param expression - The expression to analyze
 * @param validVariables - Optional list of valid variable names to filter by (e.g., from exercise widgets)
 */
function extractVariables(expression: string, validVariables?: string[]): string[] {
  const matches = expression.match(/\b[a-zA-Z_][a-zA-Z0-9_]*\b/g) || [];

  // Filter out boolean literals and keywords
  const variables = matches.filter(v =>
    v !== 'true' &&
    v !== 'false' &&
    v !== 'null' &&
    v !== 'undefined'
  );

  // Return unique variables
  const uniqueVars = Array.from(new Set(variables));

  // If validVariables is provided, only return variables that are in the valid list
  if (validVariables && validVariables.length > 0) {
    return uniqueVars.filter(v => validVariables.includes(v));
  }

  return uniqueVars;
}
